import React from "react";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router";
import {
  ActionButton,
  Pill,
  StatusBadge,
  joinClasses,
} from "../ResultsPrimitives.jsx";
import {
  buildCompactContext,
  buildDetailedContext,
  canShowSuspendFromErrorAction,
  formatTechnicalDetails,
  getLocationStatusLabel,
  resolveFilterLabel,
  resolveNonAssignableClientLabel,
  resolveSourceContextLabel,
  scopeLabel,
  severityLabel,
  shouldUseCompactMissionLayout,
} from "../../utils/errorsCenterData.js";

const severityCardClasses = {
  error: "border-transparent bg-[rgba(255,247,246,0.92)]",
  info: "border-transparent bg-[rgba(245,249,248,0.92)]",
  warning: "border-transparent bg-[rgba(255,250,242,0.94)]",
};

const locationRowClasses = {
  error: "border-[rgba(197,82,74,0.18)] bg-[rgba(255,246,245,0.98)]",
  neutral: "border-[rgba(15,92,87,0.08)] bg-white/92",
  success: "border-[rgba(148,163,184,0.18)] bg-white/94",
};

const locationIconClasses = {
  error: "bg-[rgba(197,82,74,0.12)] text-[#b33f37]",
  neutral: "bg-[rgba(15,92,87,0.08)] text-muted-strong",
  success: "bg-[rgba(148,163,184,0.16)] text-slate-500",
};

const locationIconLabels = {
  error: "!",
  neutral: "•",
  success: "✓",
};

const locationStateClasses = {
  error: "border-[rgba(197,82,74,0.12)] bg-[rgba(197,82,74,0.1)] text-[#b33f37]",
  neutral: "border-[rgba(15,92,87,0.1)] bg-[rgba(15,92,87,0.08)] text-muted-strong",
  success: "border-[rgba(148,163,184,0.18)] bg-[rgba(248,250,252,0.96)] text-slate-600",
};

const errorTagClassName = "inline-flex w-fit items-center rounded-full border border-line-soft bg-white/90 px-1.75 py-0.75 text-[0.62rem] font-black uppercase tracking-[0.1em] text-slate-600";
const compactOrderCardClassName = "border-[rgba(217,119,6,0.18)] bg-[rgba(255,250,242,0.94)]";
const suspendActionButtonClassName = "font-extrabold shadow-none hover:shadow-none";

function buildDataManagerOrderHref(orderId) {
  const resolvedOrderId = typeof orderId === "string" ? orderId.trim() : "";
  if (!resolvedOrderId) {
    return "";
  }

  const searchParams = new URLSearchParams();
  searchParams.set("tab", "orders");
  searchParams.set("orderScope", "browse");
  searchParams.set("orderSearch", resolvedOrderId);
  return `/data-manager?${searchParams.toString()}`;
}

function buildRouteSummaryPresentation(routeSummary) {
  const summary = typeof routeSummary === "string" ? routeSummary.trim() : "";
  if (!summary) {
    return { label: "Trajet", value: null };
  }

  if (summary.startsWith("Projection de la meilleure tournée")) {
    return {
      label: "Projection de tournée",
      value: summary,
    };
  }

  if (summary.startsWith("Temps estimé à la vitesse moyenne camion")) {
    return {
      label: "Temps estimé camion",
      value: summary,
    };
  }

  return { label: "Trajet camion", value: summary };
}

function buildCardHeadline(entry) {
  if (entry.filterKey === "non_assignable_orders") {
    const clientLabel = resolveNonAssignableClientLabel(entry);
    if (clientLabel) {
      return clientLabel;
    }
  }
  if (["excluded_truck_refresh", "routing_issues"].includes(entry.filterKey) && entry.truckName) {
    return entry.truckName;
  }


  return entry.orderLabel || (entry.orderId ? `Commande ${entry.orderId}` : entry.clientName || "");
}

function buildPanelId(value) {
  return String(value || "group").replace(/[^a-zA-Z0-9_-]/g, "-");
}

function buildCompactOrderSubtitle(entry) {
  const parts = [];

  if (entry.quantityTons != null && entry.quantityTons > 0) {
    parts.push(`${Number.isInteger(entry.quantityTons) ? entry.quantityTons : entry.quantityTons.toFixed(1)} t`);
  }

  if (entry.product) {
    parts.push(entry.product);
  }

  if (entry.isDompeur != null) {
    parts.push(entry.isDompeur ? "Dompeur" : "Citerne");
  }

  return parts.join(" · ");
}

function buildCardSupportingText(entry, context) {
  if (context && entry.cause) {
    return `${context} · ${entry.cause}`;
  }

  return context || entry.cause || "";
}

function ErrorMetaCard({ label, value }) {
  const content = Array.isArray(value) ? value.filter(Boolean).join("\n\n") : value;
  if (!content) {
    return null;
  }

  return (
    <div className="grid gap-1 rounded-[11px] bg-white/84 px-2 py-1.75 ring-1 ring-[rgba(15,92,87,0.06)]">
      <span className="text-[0.62rem] font-bold uppercase tracking-[0.05em] text-muted-strong">{label}</span>
      <strong className="whitespace-pre-line text-[0.82rem] leading-5 text-text">{content}</strong>
    </div>
  );
}

function ErrorMetaGrid({ entry }) {
  const orderValue = entry.orderLabel || (entry.orderId ? `Commande ${entry.orderId}` : "");
  const contextValue = buildDetailedContext(entry);
  const routeSummaryPresentation = buildRouteSummaryPresentation(entry.routeSummary);

  return (
    <div className="grid gap-1.5 md:grid-cols-[repeat(auto-fit,minmax(128px,1fr))]">
      <ErrorMetaCard label={orderValue ? "Commande" : "Lieu"} value={orderValue || entry.clientName} />
      <ErrorMetaCard label="Produit" value={entry.product} />
      <ErrorMetaCard label="Chargement" value={entry.loadingDetail} />
      <ErrorMetaCard label={entry.deliveryDetails.length > 1 ? "Déchargements" : "Déchargement"} value={entry.deliveryDetails} />
      <ErrorMetaCard label={routeSummaryPresentation.label} value={routeSummaryPresentation.value} />
      <ErrorMetaCard label="Contexte" value={contextValue} />
      <ErrorMetaCard label="Cause" value={entry.cause} />
      <ErrorMetaCard label="Action recommandée" value={entry.recommendedAction} />
    </div>
  );
}

function ErrorLocationList({ entry }) {
  const rows = [];

  if (entry.loadingItem) {
    rows.push({
      key: `${entry.errorId}-loading`,
      item: entry.loadingItem,
      label: "Chargement",
    });
  }

  entry.deliveryItems.forEach((item, index) => {
    rows.push({
      key: `${entry.errorId}-delivery-${index}`,
      item,
      label: entry.deliveryItems.length > 1 ? `Déchargement ${index + 1}` : "Déchargement",
    });
  });

  return (
    <div className="overflow-hidden rounded-[11px] bg-white/88 ring-1 ring-surface-accent-tint-soft">
      {rows.map(({ item, key, label }) => {
        const showStatus = item.status !== "neutral";

        return (
        <div
          key={key}
          className={joinClasses([
            "grid items-center gap-2 px-2.25 py-1.25 not-last:border-b not-last:border-surface-accent-tint-soft",
            showStatus ? "md:grid-cols-[auto_minmax(0,1fr)_auto]" : "md:grid-cols-[auto_minmax(0,1fr)]",
            item.status === "error" ? "bg-[rgba(255,246,245,0.78)]" : "bg-transparent",
          ])}
        >
          <span
            className={joinClasses([
              "inline-flex h-4.5 w-4.5 items-center justify-center rounded-full text-[0.67rem] font-extrabold",
              locationIconClasses[item.status] || locationIconClasses.neutral,
            ])}
          >
            {locationIconLabels[item.status] || locationIconLabels.neutral}
          </span>
          <div className="grid min-w-0 gap-0.5">
            <span className="text-[0.6rem] uppercase tracking-[0.05em] text-muted-strong">{label}</span>
            <strong className="text-[0.8rem] leading-5 text-text">
              {[item.label, item.address].filter(Boolean).join(" - ")}
            </strong>
          </div>
          {showStatus ? (
            <span
              className={joinClasses([
                "justify-self-start rounded-full border px-1.5 py-0.75 text-[0.66rem] font-bold leading-none md:justify-self-end",
                locationStateClasses[item.status] || locationStateClasses.neutral,
              ])}
            >
              {getLocationStatusLabel(item.status)}
            </span>
          ) : null}
        </div>
        );
      })}
    </div>
  );
}

function MissionSynopsisCard({ label, value }) {
  return (
    <div className="grid gap-1 bg-white/90 px-2 py-1.75">
      <span className="text-[0.62rem] font-bold uppercase tracking-[0.05em] text-muted-strong">{label}</span>
      <strong className="text-[0.8rem] leading-5 text-text">{value}</strong>
    </div>
  );
}

function MissionSummary({ entry }) {
  const synopsisGridClassName = entry.routeSummary ? "md:grid-cols-3" : "md:grid-cols-2";
  const routeSummaryPresentation = buildRouteSummaryPresentation(entry.routeSummary);

  return (
    <section className="grid gap-1.5">
      <div className={joinClasses([
        "grid gap-px overflow-hidden rounded-[11px] bg-surface-accent-tint-soft",
        synopsisGridClassName,
      ])}>
        <MissionSynopsisCard label="Cause" value={entry.cause} />
        {routeSummaryPresentation.value ? <MissionSynopsisCard label={routeSummaryPresentation.label} value={routeSummaryPresentation.value} /> : null}
        <MissionSynopsisCard label="Action" value={entry.recommendedAction} />
      </div>
      <ErrorLocationList entry={entry} />
    </section>
  );
}

function TechnicalDetailsContent({ entry, isOpen }) {
  if (!entry.isTechnicalDetailsAvailable || !isOpen) {
    return null;
  }

  return (
    <div className="grid gap-1.5 rounded-[11px] bg-[rgba(248,250,250,0.94)] px-2.5 py-2 ring-1 ring-surface-accent-tint-soft">
      <strong className="text-[0.8rem] text-text">{entry.technicalSummary}</strong>
      <pre className="m-0 whitespace-pre-wrap wrap-break-word font-mono text-[0.72rem] leading-4.5 text-muted-strong">
        {formatTechnicalDetails(entry.technicalDetails)}
      </pre>
    </div>
  );
}

function isTruckCompatibilityBreakdown(technicalDetails) {
  return Boolean(
    technicalDetails
    && !Array.isArray(technicalDetails)
    && technicalDetails.diagnosticType === "truck_compatibility_breakdown"
    && Array.isArray(technicalDetails.breakdown)
    && technicalDetails.breakdown.length,
  );
}

function formatTruckCompatibilityBreakdownSummary(technicalDetails) {
  const breakdown = Array.isArray(technicalDetails?.breakdown) ? technicalDetails.breakdown : [];
  const countedTrucks = breakdown.reduce((total, item) => total + (Number(item?.count) || 0), 0);
  const evaluatedTruckCount = Number.isFinite(Number(technicalDetails?.evaluatedTruckCount))
    ? Number(technicalDetails.evaluatedTruckCount)
    : countedTrucks;
  const assignableTruckCount = Number.isFinite(Number(technicalDetails?.assignableTruckCount))
    ? Number(technicalDetails.assignableTruckCount)
    : 0;
  const parts = [];

  if (evaluatedTruckCount > 0) {
    parts.push(`${evaluatedTruckCount} camion${evaluatedTruckCount > 1 ? "s" : ""} évalué${evaluatedTruckCount > 1 ? "s" : ""}`);
  }

  parts.push(`${assignableTruckCount} assignable${assignableTruckCount === 1 ? "" : "s"}`);
  parts.push(`${breakdown.length} cause${breakdown.length > 1 ? "s" : ""} bloquante${breakdown.length > 1 ? "s" : ""}`);

  return parts.join(" · ");
}

function formatTruckNameList(truckNames) {
  const resolvedTruckNames = Array.isArray(truckNames) ? truckNames.filter(Boolean) : [];
  if (!resolvedTruckNames.length) {
    return "";
  }

  if (resolvedTruckNames.length <= 6) {
    return resolvedTruckNames.join(", ");
  }

  const remainingCount = resolvedTruckNames.length - 6;
  return `${resolvedTruckNames.slice(0, 6).join(", ")} + ${remainingCount} autre${remainingCount > 1 ? "s" : ""}`;
}

function TruckCompatibilityBreakdownContent({ technicalDetails }) {
  if (!isTruckCompatibilityBreakdown(technicalDetails)) {
    return null;
  }

  const breakdown = technicalDetails.breakdown.filter(Boolean);
  const summary = formatTruckCompatibilityBreakdownSummary(technicalDetails);

  return (
    <section className="grid gap-1.5">
      <div className="grid gap-1 rounded-[11px] bg-white/90 px-2 py-1.75 ring-1 ring-[rgba(15,92,87,0.06)]">
        <span className="text-[0.62rem] font-bold uppercase tracking-[0.05em] text-muted-strong">Ventilation du rejet</span>
        <strong className="text-[0.82rem] leading-5 text-text">{summary}</strong>
      </div>
      <div className="grid gap-1.5 md:grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
        {breakdown.map((item, index) => {
          const truckLabel = formatTruckNameList(item.truckNames);
          return (
            <div key={`${item.reasonCode || item.reasonLabel || "reason"}-${index}`} className="grid gap-1 rounded-[11px] bg-white/90 px-2 py-1.75 ring-1 ring-[rgba(15,92,87,0.06)]">
              <div className="flex items-start justify-between gap-2">
                <strong className="text-[0.8rem] leading-5 text-text">{item.reasonLabel}</strong>
                <span className="rounded-full border border-surface-accent-tint-soft bg-[rgba(15,92,87,0.06)] px-1.5 py-0.75 text-[0.66rem] font-bold leading-none text-muted-strong">
                  {item.count} camion{item.count > 1 ? "s" : ""}
                </span>
              </div>
              {item.representativeDetail ? <p className="m-0 text-[0.75rem] leading-4.5 text-muted-strong">{item.representativeDetail}</p> : null}
              {truckLabel ? <p className="m-0 text-[0.7rem] leading-4 text-muted-strong">Camions : {truckLabel}</p> : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function ErrorGroupCard({
  canSuspendFromErrors = false,
  children = null,
  group,
  isOpen = false,
  onSuspendGroup = null,
  onToggle = null,
}) {
  const groupEntries = Array.isArray(group?.entries) ? group.entries : [];
  const groupPanelId = `error-group-panel-${buildPanelId(group?.rowId)}`;
  const showSuspendAction = groupEntries.length > 0
    && groupEntries.every((entry) => canShowSuspendFromErrorAction(entry, canSuspendFromErrors))
    && typeof onSuspendGroup === "function";
  const primaryEntry = group?.primaryEntry || groupEntries[0] || {};
  const statusTone = primaryEntry.severity === "warning" ? "warning" : primaryEntry.severity === "error" ? "danger" : "neutral";

  return (
    <div className="grid gap-1">
      <article
        className={joinClasses([
          "grid gap-1.25 rounded-[12px] border border-[rgba(217,119,6,0.18)] bg-[rgba(255,250,242,0.94)] px-2 py-1.75",
        ])}
      >
        <div className="flex flex-wrap items-start justify-between gap-1.5">
          <button
            type="button"
            className="flex min-w-[220px] flex-1 appearance-none items-start gap-1.5 border-0 bg-transparent p-0 text-left text-inherit"
            aria-expanded={isOpen ? "true" : "false"}
            aria-controls={groupPanelId}
            onClick={onToggle}
          >
            <span className="mt-0.25 inline-flex h-6.5 w-6.5 shrink-0 items-center justify-center rounded-full border border-[rgba(217,119,6,0.2)] bg-white/90 text-[#92400e] transition">
              <ChevronDown
                aria-hidden="true"
                className={joinClasses([
                  "h-3.5 w-3.5 transition-transform",
                  isOpen ? "rotate-0" : "-rotate-90",
                ])}
                strokeWidth={2.3}
              />
            </span>
            <div className="grid min-w-0 gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <p className={errorTagClassName}>{resolveFilterLabel(primaryEntry.filterKey || "non_assignable_orders")}</p>
                <span className="text-[0.68rem] font-semibold uppercase tracking-[0.06em] text-muted-strong">Groupe client</span>
                <Pill tone="neutral">{groupEntries.length} commande{groupEntries.length > 1 ? "s" : ""}</Pill>
              </div>
              <h3 className="font-display text-[0.93rem] font-semibold leading-[1.1] tracking-[-0.02em] text-text">
                {group?.title || "Client non assignable"}
              </h3>
              {group?.subtitle ? <p className="m-0 text-[0.76rem] leading-4.5 text-muted-strong">{group.subtitle}</p> : null}
            </div>
          </button>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            {!showSuspendAction ? (
              <StatusBadge tone={statusTone}>
                {severityLabel(primaryEntry.severity || "error")}
              </StatusBadge>
            ) : null}
            {showSuspendAction ? (
              <ActionButton
                tone="accent"
                size="pill"
                className={suspendActionButtonClassName}
                onClick={() => {
                  onSuspendGroup({
                    entries: groupEntries,
                    groupKey: group?.rowId,
                    groupLabel: group?.title,
                  });
                }}
              >
                Transférer dans Suspendues
              </ActionButton>
            ) : null}
          </div>
        </div>
      </article>

      {isOpen ? (
        <div id={groupPanelId} className="grid gap-1 border-l-2 border-[rgba(217,119,6,0.2)] pl-2">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export default function ErrorCard({
  canSuspendFromErrors = false,
  entry,
  isOpen,
  isTechnicalOpen,
  onSelect,
  onSuspendFromError = null,
  onToggleTechnical,
}) {
  const shouldUseCompactLayout = shouldUseCompactMissionLayout(entry);
  const context = shouldUseCompactLayout ? "" : buildCompactContext(entry);
  const headline = buildCardHeadline(entry) || entry.title;
  const supportingText = buildCardSupportingText(entry, context);
  const compactSubtitle = shouldUseCompactLayout ? buildCompactOrderSubtitle(entry) : "";
  const hasTruckCompatibilityBreakdown = isTruckCompatibilityBreakdown(entry.technicalDetails);
  const visibleSupportingText = shouldUseCompactLayout ? "" : supportingText;
  const subtitleParts = [];

  if (!shouldUseCompactLayout) {
    if (headline !== entry.title) {
      subtitleParts.push(entry.title);
    }
    if (entry.quantityTons != null && entry.quantityTons > 0) {
      subtitleParts.push(`${Number.isInteger(entry.quantityTons) ? entry.quantityTons : entry.quantityTons.toFixed(1)} t`);
    }
    if (entry.product) {
      subtitleParts.push(entry.product);
    }
    if (entry.isDompeur != null) {
      subtitleParts.push(entry.isDompeur ? "Dompeur" : "Citerne");
    }
  }

  const subtitle = subtitleParts.join(" · ");
  const showScopePill = Boolean(entry.scope) && entry.scope !== "assignment";
  const dataManagerOrderHref = buildDataManagerOrderHref(entry.orderId);
  const showTechnicalToggle = entry.isTechnicalDetailsAvailable;
  const showSuspendAction = canShowSuspendFromErrorAction(entry, canSuspendFromErrors) && typeof onSuspendFromError === "function";

  return (
    <article
      className={joinClasses([
        "grid gap-1.25 rounded-[12px] border px-2 py-1.75",
        shouldUseCompactLayout
          ? compactOrderCardClassName
          : (severityCardClasses[entry.severity] || severityCardClasses.info),
      ])}
    >
      <div className="flex flex-wrap items-start justify-between gap-1.25">
        <button
          type="button"
          className="flex min-w-[220px] flex-1 appearance-none items-start gap-1.5 border-0 bg-transparent p-0 text-left text-inherit"
          aria-expanded={isOpen ? "true" : "false"}
          onClick={onSelect}
        >
          {shouldUseCompactLayout ? (
            <>
              <span className="mt-0.25 inline-flex h-6.5 w-6.5 shrink-0 items-center justify-center rounded-full border border-[rgba(217,119,6,0.2)] bg-white/90 text-[#92400e] transition">
                <ChevronDown
                  aria-hidden="true"
                  className={joinClasses([
                    "h-3.5 w-3.5 transition-transform",
                    isOpen ? "rotate-0" : "-rotate-90",
                  ])}
                  strokeWidth={2.3}
                />
              </span>
              <div className="grid min-w-0 gap-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className={errorTagClassName}>{resolveFilterLabel(entry.filterKey)}</p>
                  <span className="text-[0.68rem] font-semibold uppercase tracking-[0.06em] text-muted-strong">Commande</span>
                </div>
                <h3 className="font-display text-[0.93rem] font-semibold leading-[1.1] tracking-[-0.02em] text-text">
                  {headline}
                </h3>
                {compactSubtitle ? <p className="m-0 text-[0.76rem] leading-4.5 text-muted-strong">{compactSubtitle}</p> : null}
              </div>
            </>
          ) : (
            <div className="grid min-w-0 gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <p className={errorTagClassName}>{resolveFilterLabel(entry.filterKey)}</p>
                {subtitle ? <span className="text-[0.68rem] font-semibold uppercase tracking-[0.06em] text-muted-strong">{subtitle}</span> : null}
              </div>
              <h3 className="font-display text-[0.9rem] font-semibold leading-[1.08] tracking-[-0.02em] text-text">
                {headline}
              </h3>
              {visibleSupportingText ? <p className="m-0 text-[0.76rem] leading-4.5 text-muted-strong">{visibleSupportingText}</p> : null}
            </div>
          )}
        </button>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {!showSuspendAction ? (
            <StatusBadge tone={entry.severity === "error" ? "danger" : entry.severity === "warning" ? "warning" : "neutral"}>
              {severityLabel(entry.severity)}
            </StatusBadge>
          ) : null}
          {showScopePill ? <Pill tone="neutral">{scopeLabel(entry.scope)}</Pill> : null}
          {showSuspendAction ? (
            <ActionButton
              tone="accent"
              size="pill"
              className={suspendActionButtonClassName}
              onClick={() => {
                onSuspendFromError(entry);
              }}
            >
              Transférer dans Suspendues
            </ActionButton>
          ) : null}
        </div>
      </div>

      {isOpen ? (
        <div className="grid gap-1.5">
          {shouldUseCompactLayout ? (
            <MissionSummary entry={entry} />
          ) : (
            <>
              <p className="m-0 text-[0.8rem] leading-5 text-muted-strong">{entry.cause}</p>
              <ErrorMetaGrid entry={entry} />
            </>
          )}
          {hasTruckCompatibilityBreakdown ? <TruckCompatibilityBreakdownContent technicalDetails={entry.technicalDetails} /> : null}
          {showTechnicalToggle || dataManagerOrderHref ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {showTechnicalToggle ? (
                <ActionButton
                  tone="ghost"
                  size="sm"
                  aria-expanded={isTechnicalOpen ? "true" : "false"}
                  onClick={onToggleTechnical}
                >
                  {isTechnicalOpen ? "Masquer les détails techniques" : "Afficher les détails techniques"}
                </ActionButton>
              ) : null}
              {dataManagerOrderHref ? (
                <ActionButton
                  as={Link}
                  to={dataManagerOrderHref}
                  tone="subtle"
                  size="sm"
                >
                  Ouvrir dans Data Manager
                </ActionButton>
              ) : null}
            </div>
          ) : null}
          {showTechnicalToggle ? <TechnicalDetailsContent entry={entry} isOpen={isTechnicalOpen} /> : null}
        </div>
      ) : null}
    </article>
  );
}
